import { motion } from 'framer-motion';
import { Frame, Type } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useQRStore, FrameStyle } from '@/stores/qrStore';
import { frameStyles } from '@/lib/design-constants';
import { cn } from '@/lib/utils';

export function FrameTab() {
  const { currentQR, setFrameSettings } = useQRStore();
  const { frame } = currentQR.designSettings;

  return (
    <div className="space-y-6">
      {/* Frame Styles */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Frame className="h-4 w-4 text-primary" />
          <Label className="text-sm font-medium">Frame Style</Label>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          {frameStyles.map((style, index) => {
            const isActive = frame.style === style.id;
            
            return (
              <motion.button
                key={style.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.02 }}
                onClick={() => setFrameSettings({ style: style.id as FrameStyle })}
                className={cn(
                  'relative p-3 rounded-xl border transition-all duration-200 text-center',
                  isActive
                    ? 'border-primary bg-primary/10'
                    : 'border-border/50 hover:border-primary/50 hover:bg-muted/50'
                )}
              >
                {/* Frame Preview Thumbnail */}
                <div className="w-full aspect-square mb-2 rounded-lg bg-muted/50 flex items-center justify-center overflow-hidden">
                  <FramePreviewThumbnail style={style.id} isActive={isActive} />
                </div>
                <span className="text-xs font-medium line-clamp-1">{style.name}</span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Frame Settings - only show if frame is not 'none' */}
      {frame.style !== 'none' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-6"
        >
          {/* Custom Text */}
          {['scan-me-top', 'scan-me-bottom', 'tag', 'badge', 'clipboard', 'laptop', 'watch', 'camera', 'bag', 'speech-bubble', 'banner'].includes(frame.style) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-primary" />
                <Label>Frame Text</Label>
              </div>
              <Input
                value={frame.text}
                onChange={(e) => setFrameSettings({ text: e.target.value })}
                placeholder="SCAN ME"
                maxLength={30}
              />
            </div>
          )}

          {/* Frame Color */}
          <div className="space-y-2">
            <Label>Frame Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={frame.color}
                onChange={(e) => setFrameSettings({ color: e.target.value })}
                className="w-12 h-10 p-1 cursor-pointer"
              />
              <Input
                type="text"
                value={frame.color}
                onChange={(e) => setFrameSettings({ color: e.target.value })}
                className="flex-1 font-mono uppercase"
                maxLength={7}
              />
            </div>
          </div>

          {/* Frame Thickness */}
          <div className="space-y-2">
            <div className="flex justify-between">
              <Label>Frame Thickness</Label>
              <span className="text-sm text-muted-foreground">{frame.thickness}px</span>
            </div>
            <Slider
              value={[frame.thickness]}
              onValueChange={([value]) => setFrameSettings({ thickness: value })}
              min={5}
              max={50}
              step={1}
            />
          </div>

          {/* Text Position */}
          {['scan-me-top', 'scan-me-bottom', 'tag', 'badge', 'clipboard', 'laptop', 'watch', 'camera', 'bag', 'speech-bubble', 'banner'].includes(frame.style) && (
            <div className="space-y-2">
              <Label>Text Position</Label>
              <RadioGroup
                value={frame.textPosition}
                onValueChange={(value) => setFrameSettings({ textPosition: value as 'top' | 'bottom' | 'inside' })}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="top" id="top" />
                  <Label htmlFor="top" className="cursor-pointer">Top</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bottom" id="bottom" />
                  <Label htmlFor="bottom" className="cursor-pointer">Bottom</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="inside" id="inside" />
                  <Label htmlFor="inside" className="cursor-pointer">Inside</Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

function FramePreviewThumbnail({ style, isActive }: { style: string; isActive: boolean }) {
  const baseColor = isActive ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))';
  
  return (
    <div className="relative w-8 h-8">
      {/* QR placeholder */}
      <div 
        className="absolute inset-1 rounded-sm"
        style={{ 
          background: `repeating-conic-gradient(${baseColor} 0% 25%, transparent 0% 50%) 50% / 4px 4px`,
          opacity: 0.6
        }}
      />
      
      {/* Frame visualization */}
      {style === 'simple' && (
        <div className="absolute inset-0 border-2 rounded" style={{ borderColor: baseColor }} />
      )}
      {style === 'rounded' && (
        <div className="absolute inset-0 border-2 rounded-lg" style={{ borderColor: baseColor }} />
      )}
      {style === 'circle' && (
        <div className="absolute inset-0 border-2 rounded-full" style={{ borderColor: baseColor }} />
      )}
      {style === 'double' && (
        <>
          <div className="absolute inset-0 border rounded" style={{ borderColor: baseColor }} />
          <div className="absolute inset-0.5 border rounded" style={{ borderColor: baseColor }} />
        </>
      )}
      {(style === 'scan-me-top' || style === 'scan-me-bottom') && (
        <>
          <div className="absolute inset-0 border-2 rounded" style={{ borderColor: baseColor }} />
          <div 
            className={cn(
              "absolute left-0 right-0 h-1.5 rounded-sm",
              style === 'scan-me-top' ? '-top-2' : '-bottom-2'
            )}
            style={{ backgroundColor: baseColor }}
          />
        </>
      )}
      {style === 'phone' && (
        <div className="absolute inset-0 border-2 rounded-xl" style={{ borderColor: baseColor }}>
          <div className="absolute top-0.5 left-1/2 -translate-x-1/2 w-3 h-0.5 rounded-full" style={{ backgroundColor: baseColor, opacity: 0.4 }} />
          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full border" style={{ borderColor: baseColor, opacity: 0.4 }} />
        </div>
      )}
      {style === 'instagram' && (
        <div className="absolute inset-0 border-2 rounded-lg" style={{ borderColor: baseColor }}>
          <div className="absolute -inset-0.5 rounded-lg bg-gradient-to-br from-[#f09433] via-[#e6683c] to-[#bc1888] -z-10 opacity-60" />
        </div>
      )}
      {style === 'tag' && (
        <div className="absolute inset-0 border-2 rounded border-b-4" style={{ borderColor: baseColor }}>
          <div className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-6 h-1 rounded-sm" style={{ backgroundColor: baseColor, opacity: 0.5 }} />
        </div>
      )}
      {style === 'decorative' && (
        <>
          <div className="absolute top-0 left-0 w-2.5 h-2.5 border-t-2 border-l-2 rounded-tl" style={{ borderColor: baseColor }} />
          <div className="absolute top-0 right-0 w-2.5 h-2.5 border-t-2 border-r-2 rounded-tr" style={{ borderColor: baseColor }} />
          <div className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b-2 border-l-2 rounded-bl" style={{ borderColor: baseColor }} />
          <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b-2 border-r-2 rounded-br" style={{ borderColor: baseColor }} />
        </>
      )}
      {style === 'badge' && (
        <div className="absolute inset-0 border-2 rounded-full" style={{ borderColor: baseColor }}>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-1 rounded-sm" style={{ backgroundColor: baseColor, opacity: 0.5 }} />
        </div>
      )}
      {style === 'ticket' && (
        <div className="absolute inset-0 border-2 border-dashed rounded" style={{ borderColor: baseColor }} />
      )}
      {style === 'gradient' && (
        <div className="absolute inset-0 rounded-lg" style={{ background: `linear-gradient(135deg, ${baseColor}, transparent, ${baseColor})`, padding: '2px' }}>
          <div className="w-full h-full rounded-lg bg-background" />
        </div>
      )}
      {style === 'neon' && (
        <div 
          className="absolute inset-0 rounded-lg"
          style={{ 
            boxShadow: `0 0 4px ${baseColor}, 0 0 8px ${baseColor}`,
            border: `1px solid ${baseColor}`
          }}
        />
      )}
      {style === 'clipboard' && (
        <div className="absolute inset-0 border-x-2 border-b-2 rounded-b" style={{ borderColor: baseColor }}>
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-4 h-1 border border-b-0 rounded-t" style={{ borderColor: baseColor }} />
        </div>
      )}
      {style === 'laptop' && (
        <div className="absolute inset-0 border-2 rounded-t" style={{ borderColor: baseColor }}>
          <div className="absolute -bottom-1 -left-1 -right-1 h-1 rounded-b" style={{ backgroundColor: baseColor }} />
        </div>
      )}
      {style === 'watch' && (
        <div className="absolute inset-0 border-2 rounded-xl" style={{ borderColor: baseColor }}>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-2 rounded-t" style={{ backgroundColor: baseColor }} />
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-2 rounded-b" style={{ backgroundColor: baseColor }} />
        </div>
      )}
      {style === 'camera' && (
        <div className="absolute inset-0 border-2 rounded" style={{ borderColor: baseColor }}>
          <div className="absolute -top-1 left-1 w-2 h-1 rounded-t" style={{ backgroundColor: baseColor }} />
          <div className="absolute top-0.5 right-0.5 w-1 h-1 rounded-full bg-current opacity-50" />
        </div>
      )}
      {style === 'bag' && (
        <div className="absolute inset-0 border-2 rounded-b-xl" style={{ borderColor: baseColor }}>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-2 border-2 border-b-0 rounded-t-full" style={{ borderColor: baseColor }} />
        </div>
      )}
      {style === 'speech-bubble' && (
        <div className="absolute inset-0 border-2 rounded-lg" style={{ borderColor: baseColor }}>
          <div className="absolute -bottom-1 left-2 w-1.5 h-1.5 border-r-2 border-b-2 rotate-45" style={{ borderColor: baseColor, backgroundColor: 'white' }} />
        </div>
      )}
      {style === 'banner' && (
        <div className="absolute inset-x-0 inset-y-1 border-y-2 border-x-4 border-x-transparent" style={{ borderColor: baseColor }} />
      )}
      {['clipboard', 'laptop', 'watch', 'camera', 'bag', 'speech-bubble'].includes(style) && (
        <div className="absolute inset-0 border-2 rounded opacity-80" style={{ borderColor: baseColor, borderStyle: 'dashed' }} />
      )}
    </div>
  );
}
